import React, { useState, useEffect } from "react";
import axiosInstance from "../../Axios/axiosInstance";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Snackbar,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

const PitchedList = () => {
  const navigate = useNavigate();
  const [pitched, setPitched] = useState([]);
  const [filteredPitched, setFilteredPitched] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedPitched, setSelectedPitched] = useState(null);
  const [editedPitched, setEditedPitched] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] =
    useState(false);
  const [deletePitchedId, setDeletePitchedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get("api/getPitched");

        if (response && response.data) {
          setPitched(response.data);
        } else {
          // Error handling for failed request
        }
      } catch (error) {
        // Catch and handle network errors
      }
    };

    fetchData();
  }, [pitched]);

  useEffect(() => {
    filterPitched();
  }, [searchInput, pitched, currentPage, rowsPerPage]);

  const filterPitched = () => {
    const lowerCaseSearchInput = searchInput.toLowerCase();
    const filtered = pitched.filter(
      (pitch) =>
        pitch.leadname?.toLowerCase().includes(lowerCaseSearchInput) ||
        pitch.contactno?.toLowerCase().includes(lowerCaseSearchInput) ||
        pitch.leademail?.toLowerCase().includes(lowerCaseSearchInput)
    );
    setFilteredPitched(filtered);
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value.trim());
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const indexOfLastPitched = currentPage * rowsPerPage;
  const indexOfFirstPitched = indexOfLastPitched - rowsPerPage;

  const currentPitched = filteredPitched.slice(
    indexOfFirstPitched,
    indexOfLastPitched
  );

  const handleDelete = (pitchId) => {
    setDeletePitchedId(pitchId);
    setDeleteConfirmationDialog(true);
  };

  const handleConfirmDelete = async (pitchId) => {
    try {
      const response = await axiosInstance.delete(
        `/api/deletePitched/${pitchId}`
      );

      if (response && response.status === 200) {
        setPitched((prevPitched) =>
          prevPitched.filter((pitch) => pitch.id !== pitchId)
        );
        setOpenSnackbar(true);
        setSubmitMessage("Pitched lead deleted successfully!");
        setTimeout(() => {
          setOpenSnackbar(false);
          setSubmitMessage("");
        }, 3000);
      } else {
        console.error("Failed to delete pitched lead");
      }
    } catch (error) {
      console.error("Error deleting pitched lead:", error);
    }

    setDeleteConfirmationDialog(false);
  };

  const handleToggleEditDialog = () => {
    setOpenEditDialog((prev) => !prev);
  };

  const handleEdit = (pitch) => {
    navigate(`/dashboard/pitched-update/${pitch.id}`);
    console.log(pitch.id);
  };

  const handleSaveChanges = async () => {
    try {
      const response = await fetch(`/api/updatePitched/${selectedPitched.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(editedPitched),
      });

      if (response.ok) {
        setPitched((prevPitched) =>
          prevPitched.map((pitch) =>
            pitch.id === selectedPitched.id ? editedPitched : pitch
          )
        );
        handleToggleEditDialog();
        setOpenSnackbar(true);
        setSubmitMessage("Pitched lead data updated successfully!");
      } else {
        console.error("Failed to update pitched lead");
      }
    } catch (error) {
      console.error("Error updating pitched lead:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedPitched((prevPitch) => ({ ...prevPitch, [name]: value }));
  };

  const handleStatusChange = async (event, pitchId) => {
    try {
      const updatedPitched = pitched.map((pitch) =>
        pitch.id === pitchId
          ? { ...pitch, pitchstatus: event.target.value }
          : pitch
      );
      setPitched(updatedPitched);

      const response = await axiosInstance.put(
        `/api/updatePitchedStatus/${pitchId}`,
        {
          pitchstatus: event.target.value,
        }
      );

      if (response && response.status === 200) {
        setOpenSnackbar(true);
        setSubmitMessage("Pitched lead status updated successfully!");
      } else {
        console.error("Failed to update pitched lead status");
      }
    } catch (error) {
      console.error("Error updating pitched lead status:", error);
    }
  };

  return (
    <Paper>
      {/* Search bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        {/* Search bar on the left */}
        <TextField
          label="Search"
          variant="outlined"
          margin="normal"
          value={searchInput}
          onChange={handleSearchInputChange}
        />

        {/* Rows per page dropdown on the right */}
        <Select
          label="Rows per page"
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          style={{ marginLeft: "auto", marginRight: "15%" }}
          displayEmpty
        >
          <MenuItem value={10}>Show 10 rows</MenuItem>
          <MenuItem value={50}>Show 50 rows</MenuItem>
          <MenuItem value={100}>Show 100 rows</MenuItem>
          <MenuItem value={500}>Show 500 rows</MenuItem>
          <MenuItem value={pitched.length}>Show All rows</MenuItem>
        </Select>
      </div>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Lead Name</TableCell>
              <TableCell>Contact No</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Lead status</TableCell>
              <TableCell>Change Status</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPitched.map((pitch) => (
              <TableRow key={pitch.id}>
                <TableCell>{pitch.leadname}</TableCell>
                <TableCell>{pitch.contactno}</TableCell>
                <TableCell>{pitch.leademail}</TableCell>
                <TableCell>{pitch.leadlocation}</TableCell>
                <TableCell>{pitch.leadsource}</TableCell>
                <TableCell>{pitch.leadcomments}</TableCell>
                <TableCell>{pitch.changestatus}</TableCell>
                <TableCell>
                  <Select
                    value={pitch.changeStatus || ""}
                    onChange={(e) => handleStatusChange(e, pitch.id)}
                  >
                    <MenuItem value="negotiation">Negotiation</MenuItem>
                    <MenuItem value="demo">Demo</MenuItem>
                    <MenuItem value="closedlost">Closed Lost</MenuItem>
                    <MenuItem value="closedwon">Closed Won</MenuItem>
                    <MenuItem value="rnr">RNR</MenuItem>
                    <MenuItem value="junk">Junk</MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(pitch)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleConfirmDelete(pitch.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
                <TableCell>{pitch.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div
        style={{
          marginLeft: "auto",
          marginRight: "15%",
          textAlign: "right",
          marginTop: "3%",
        }}
      >
        <Button
          onClick={() => handleChangePage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span>
          Page {currentPage} of{" "}
          {Math.ceil(filteredPitched.length / rowsPerPage)}
        </span>
        <Button
          onClick={() => handleChangePage(currentPage + 1)}
          disabled={indexOfLastPitched >= filteredPitched.length}
        >
          Next
        </Button>
      </div>

      {/* Pagination info */}
      <p>
        Showing {indexOfFirstPitched + 1} to{" "}
        {Math.min(indexOfLastPitched, filteredPitched.length)} of{" "}
        {filteredPitched.length} entries
      </p>
    </Paper>
  );
};

export default PitchedList;
